/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Divider,
  Flex,
  MenuItem,
  Scrollbar,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import customerConfig from 'src/config';
import { useActiveMenuStyle } from './style';
import { PermissionNameEnum } from '@Types/permissions';
import { useOrgStore } from '../../models/useOrgModel';
import { usePermissionStore } from '../../models/usePermissionModel';
import { isInUIC } from '../../utils/common';
import { OverAllPolicyType } from './utils/types';
import { OverAllPolicyList } from './OverAllPolicyList';
import ApprovedAndBlockedList from '../GlobalSetting/ApprovedAndBlockedList';
import { useGlobalSettingStore } from '../../models/useGlobalSettingModel';
import PrivateRoute from '@Components/Common/PrivateRouter';
import Organization from '@Components/Common/Organization';
import HighProfileList from '../GlobalSetting/HighProfileList';
import InternalDomainList from '../GlobalSetting/InternalDomainList';
import PredictiveMachineLearningExceptionList from '../GlobalSetting/PredictiveMachineLearningExceptionList';
import DisplayNameSpoofingDetectionExceptionList from '../GlobalSetting/DisplayNameSpoofingDetectionExceptionList';
import NotificationSettings from '../GlobalSetting/NotificationSettings';
import SuspiciousObjectSettings from '../GlobalSetting/SuspiciousObjectSettings';
import TimeOfClickProtectionSettings from '../GlobalSetting/TimeOfClickProtectionSettings';
import AttachmentPasswordGuessing from '../GlobalSetting/AttachmentPasswordGuessing';
import ConditionalAccessPoliciesForRiskyUsers from '../GlobalSetting/ConditionalAccessPoliciesForRiskyUsers';
import MisdirectedEmailDetection from '../GlobalSetting/MisdirectedEmailDetection';
import MicrosoftLicensingModelSettingsForTeamsChat from '../GlobalSetting/MicrosoftLicensingModelSettingsForTeamsChat';
import InlineProtectionSettingsForExchangeOnline from '../GlobalSetting/InlineProtectionSettingsForExchangeOnline';
import InlineProtectionSettingsForGmail from '../GlobalSetting/InlineProtectionSettingsForGmail';
import ExchangeOnlineApiProtectionSettings from '../GlobalSetting/ExchangeOnlineApiProtectionSettings';
import { GlobalSettings } from '../GlobalSetting';
import NoPermission from '@Components/Common/NoPermission';
import { useLayoutStore } from '@Models/useLayoutModel';
import {
  CorrelatedTemplate,
  CorrelatedTemplateUrl,
} from '../GlobalSetting/CorrelatedRuleTemplates/utils';
import CorrelatedRulesAndSignalsTemplate from '../GlobalSetting/CorrelatedRuleTemplates';
import { Shell } from '@Components/Shell';
import { PocUserManagement } from '../GlobalSetting/PocUserManagement';

// import { getExchangeTemplateConfig } from './TemplateConfig/cfg.exchange';
const Policies = () => {
  const { t } = useTranslation();
  // const loginUserModel = useLoginUserStore();
  const { switchOrgId, defaultSingleOrgId } = useOrgStore();
  if (defaultSingleOrgId) {
    switchOrgId(defaultSingleOrgId);
  }
  const { widthReadablePermission, managementScopeIds } = usePermissionStore();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [selectedKey, setSelectedKey] = React.useState<string>('');
  const navigate = useNavigate();
  const activeMenuStyle = useActiveMenuStyle();
  const { pathname } = useLocation();
  const { selectedOrgGlobalSetting } = useGlobalSettingStore();
  const { layoutHeight } = useLayoutStore();

  const globalSettingsCorrelatedRules = [
    {
      name: t('policy.globalSettings.correlatedRules.ruleLists'),
      url: 'global-settings/correlated-rule-list',
      element: (
        <CorrelatedRulesAndSignalsTemplate tab={CorrelatedTemplate.rules} />
      ),
      show: !pathname.includes(CorrelatedTemplateUrl.expressions),
      shouldBeAllScope: true,
    },
    {
      name: t('policy.globalSettings.correlatedRules.ruleLists'),
      url: 'global-settings/detection-signal-list',
      element: (
        <CorrelatedRulesAndSignalsTemplate
          tab={CorrelatedTemplate.expressions}
        />
      ),
      show: pathname.includes(CorrelatedTemplateUrl.expressions),
      shouldBeAllScope: true,
    },
  ];

  const globalSettingsUserDefinedLists = [
    {
      key: 'pocUserManagement',
      name: t('policy.globalSettings.userDefined.pocUserManagement'),
      url: 'global-settings/synchronized-user-lists',
      element: <PocUserManagement />,
      show: true,
      shouldBeAllScope: true,
    },
    {
      name: t('policy.globalSettings.userDefined.approvedList'),
      url: 'global-settings/approved-and-blocked-list',
      element: <ApprovedAndBlockedList />,
      show: true,
      shouldBeAllScope: true,
    },
    {
      name: t('policy.globalSettings.userDefined.highProfile'),
      url: 'global-settings/high-profile-list',
      element: <HighProfileList />,
      shouldBeAllScope: true,
      show: customerConfig.ui.globalSettings.featureShow.highProfileList,
    },
    {
      name: t('policy.globalSettings.userDefined.internalDomain'),
      url: 'global-settings/internal-domain-list',
      element: <InternalDomainList />,
      shouldBeAllScope: true,
      show: customerConfig.ui.globalSettings.featureShow.internalDomainList,
    },
    {
      url: 'global-settings/predictive-machine-learning-exception-list',
      name: t('policy.globalSettings.userDefined.machineLearning'),
      element: <PredictiveMachineLearningExceptionList />,
      shouldBeAllScope: true,
      show: customerConfig.ui.globalSettings.featureShow.machineLearning,
    },
    {
      url: 'global-settings/display-name-spoofing-detection-exception-list',
      name: t('policy.globalSettings.userDefined.SpoofingDetection'),
      show: true,
      element: <DisplayNameSpoofingDetectionExceptionList />,
      shouldBeAllScope: true,
    },
  ];

  const globalSettingsSettings = [
    {
      url: 'global-settings/notification-settings',
      name: t('policy.globalSettings.settings.notification'),
      show: true,
      element: <NotificationSettings />,
      shouldBeAllScope: true,
    },
    {
      url: 'global-settings/suspicious-object-settings',
      name: t('policy.globalSettings.settings.suspiciousObject'),
      show: true,
      element: <SuspiciousObjectSettings />,
      shouldBeAllScope: true,
    },
    {
      name: t('policy.globalSettings.settings.timeOfClickProtection'),
      url: 'global-settings/time-of-click-protection-settings',
      show: true,
      element: <TimeOfClickProtectionSettings />,
      shouldBeAllScope: false,
    },
    {
      name: t('policy.globalSettings.settings.attachmentPassword'),
      url: 'global-settings/attachment-password-guessing',
      show: true,
      element: <AttachmentPasswordGuessing />,
      shouldBeAllScope: false,
    },
    {
      name: t('policy.globalSettings.settings.conditionalAccess'),
      url: 'global-settings/conditional-access-policies-for-risky-users',
      show: true,
      element: <ConditionalAccessPoliciesForRiskyUsers />,
      shouldBeAllScope: false,
    },
    {
      name: t('policy.globalSettings.settings.misdirectedEmailDetection'),
      url: 'global-settings/misdirected-email-detection',
      show: customerConfig.ui.globalSettings.featureShow
        .misdirectedEmailDetection,
      element: <MisdirectedEmailDetection />,
      shouldBeAllScope: false,
    },
    {
      name: t('policy.globalSettings.settings.microsoftLicensingModelForTeams'),
      url: 'global-settings/microsoft-licensing-model-settings-for-teams-chat',
      show: true,
      element: <MicrosoftLicensingModelSettingsForTeamsChat />,
      shouldBeAllScope: false,
    },
    {
      name: t(
        'policy.globalSettings.settings.inlineProtectionForExchangeOnline'
      ),
      url: 'global-settings/inline-protection-settings-for-exchange-online',
      show: !!selectedOrgGlobalSetting.exchangeInlineFeatureShow,
      element: <InlineProtectionSettingsForExchangeOnline />,
      shouldBeAllScope: false,
    },
    {
      name: t('policy.globalSettings.settings.inlineProtectionForGmail'),
      url: 'global-settings/inline-protection-settings-for-gmail',
      show: !!selectedOrgGlobalSetting.gmailInlineFeatureShow,
      element: <InlineProtectionSettingsForGmail />,
      shouldBeAllScope: false,
    },
    {
      name: t('policy.globalSettings.settings.exchangeOnlineApiProtection'),
      url: 'global-settings/exchange-online-api-protection-settings',
      show: true,
      element: <ExchangeOnlineApiProtectionSettings />,
      shouldBeAllScope: false,
    },
  ];

  useEffect(() => {
    const path = pathname.split('policy/');
    if (path[1]) {
      setSelectedKey(path[1]);
    }
  }, [pathname]);

  const handleClick = (url: string) => {
    setSelectedKey(url);
    //do not add handleRedirectClick here, it will cause the page to refresh
    if (isInUIC()) {
      if (window.top) {
        const address = new URL(
          `${window.top.location.origin}/#/app/email-and-collaboration/policy/${url}`
        );
        window.top.history.replaceState({}, '', address);
      }
    }
    navigate(url);
  };

  const emailPolicies = [
    {
      url: 'advanced-threat-protection/email',
      name: t('policy.emailPolicy.atp'),
      show:
        customerConfig.ui.policy.featureShow.emailAdvancedThreatProtection &&
        widthReadablePermission(PermissionNameEnum.atpPolicy),
    },
    {
      url: 'data-loss-prevention/email',
      name: t('policy.emailPolicy.dlp'),
      show:
        customerConfig.ui.policy.featureShow.emailDataLossPrevention &&
        widthReadablePermission(PermissionNameEnum.dlpPolicy),
    },
  ];

  const collaborationPolicies = [
    {
      url: 'advanced-threat-protection/collaboration',
      name: t('policy.collaborationService.atp'),
      show:
        customerConfig.ui.policy.featureShow
          .collaborationAdvancedThreatProtection &&
        widthReadablePermission(PermissionNameEnum.atpPolicy),
    },
    {
      url: 'data-loss-prevention/collaboration',
      name: t('policy.collaborationService.dlp'),
      show:
        customerConfig.ui.policy.featureShow.collaborationDataLossPrevention &&
        widthReadablePermission(PermissionNameEnum.dlpPolicy),
    },
  ];

  const getFirstComplement = () => {
    if (widthReadablePermission(PermissionNameEnum.atpPolicy)) {
      return <OverAllPolicyList overAllPolicyType={OverAllPolicyType.ATP} />;
    } else if (widthReadablePermission(PermissionNameEnum.dlpPolicy)) {
      return <OverAllPolicyList overAllPolicyType={OverAllPolicyType.DLP} />;
    } else if (widthReadablePermission(PermissionNameEnum.administrator)) {
      if (managementScopeIds === 'all') {
        return <ApprovedAndBlockedList />;
      } else {
        return <TimeOfClickProtectionSettings />;
      }
    } else {
      return <NoPermission />;
    }
  };

  return (
    <Box bg={colorStyle.background.primary}>
      <Flex flexDirection="row">
        <Scrollbar
          width="270px"
          height={layoutHeight}
          overflow="scroll"
          flexShrink={0}
        >
          <Flex
            borderRight="1px solid"
            borderRightColor={colorStyle.background.selected}
            flexDirection="column"
            minHeight="100%"
          >
            <Box m="20px 12px 16px 12px">
              <Text
                color={colorStyle.color.secondary}
                size={12}
                fontWeight={400}
                mb={12}
              >
                {t('policy.organization')}:
              </Text>
              <Organization
                allOrgItem={false}
                showLabel={false}
                minWidth={220}
                maxWidth={220}
              />
            </Box>
            <Divider width="270px" />
            {(widthReadablePermission(PermissionNameEnum.atpPolicy) ||
              widthReadablePermission(PermissionNameEnum.dlpPolicy)) && (
              <Text
                m="20px 12px 8px 12px"
                color={colorStyle.color.secondary}
                fontWeight={600}
              >
                {t('policy.policyManagement')}
              </Text>
            )}
            {emailPolicies.filter((item) => item.show).length > 0 && (
              <Text
                m="16px 12px 8px 12px"
                size={12}
                color={colorStyle.color.secondary}
              >
                {t('policy.emailPolicy')}
              </Text>
            )}
            {emailPolicies.map(
              (policy) =>
                policy.show && (
                  <MenuItem
                    {...(selectedKey === policy.url ? activeMenuStyle : {})}
                    key={policy.url}
                    onClick={() => handleClick(policy.url)}
                  >
                    <Text pl={12}>{policy.name}</Text>
                  </MenuItem>
                )
            )}
            {collaborationPolicies.filter((item) => item.show).length > 0 && (
              <Text
                m="8px 12px 8px 12px"
                size={12}
                color={colorStyle.color.secondary}
              >
                {t('policy.collaborationService')}
              </Text>
            )}
            {collaborationPolicies.map(
              (policy) =>
                policy.show && (
                  <MenuItem
                    {...(selectedKey === policy.url ? activeMenuStyle : {})}
                    key={policy.url}
                    onClick={() => handleClick(policy.url)}
                  >
                    <Text pl={12}>{policy.name}</Text>
                  </MenuItem>
                )
            )}
            <GlobalSettings
              globalSettingsCorrelatedRules={globalSettingsCorrelatedRules}
              globalSettingsUserDefinedLists={globalSettingsUserDefinedLists}
              globalSettingsSettings={globalSettingsSettings}
              handleClick={handleClick}
              selectedKey={selectedKey}
            />
          </Flex>
        </Scrollbar>
        <Scrollbar height={layoutHeight} width="100%" overflow="scroll">
          <Shell>
            <Routes>
              <Route
                path={`/`}
                element={getFirstComplement()}
                key={'/'}
              ></Route>

              <Route
                path={`advanced-threat-protection/:collectionType`}
                element={
                  <PrivateRoute
                    readPermissionName={[PermissionNameEnum.atpPolicy]}
                  >
                    <OverAllPolicyList
                      overAllPolicyType={OverAllPolicyType.ATP}
                    />
                  </PrivateRoute>
                }
                key={'advanced-threat-protection/:collectionType'}
              ></Route>
              <Route
                path={`data-loss-prevention/:collectionType`}
                element={
                  <PrivateRoute
                    readPermissionName={[PermissionNameEnum.dlpPolicy]}
                  >
                    <OverAllPolicyList
                      overAllPolicyType={OverAllPolicyType.DLP}
                    />
                  </PrivateRoute>
                }
                key={'data-loss-prevention/:collectionType'}
              ></Route>
              {globalSettingsCorrelatedRules.map((item) => (
                <Route
                  path={item.url}
                  element={
                    <PrivateRoute
                      readPermissionName={[PermissionNameEnum.administrator]}
                      shouldBeAllScope={item.shouldBeAllScope}
                    >
                      {item.element}
                    </PrivateRoute>
                  }
                  key={item.url}
                />
              ))}
              {globalSettingsUserDefinedLists.map((item) => (
                <Route
                  path={item.url}
                  element={
                    <PrivateRoute
                      readPermissionName={[PermissionNameEnum.administrator]}
                      shouldBeAllScope={item.shouldBeAllScope}
                    >
                      {item.element}
                    </PrivateRoute>
                  }
                  key={item.url}
                />
              ))}
              {globalSettingsSettings.map((item) => (
                <Route
                  path={item.url}
                  element={
                    <PrivateRoute
                      readPermissionName={[PermissionNameEnum.administrator]}
                      shouldBeAllScope={item.shouldBeAllScope}
                    >
                      {item.element}
                    </PrivateRoute>
                  }
                  key={item.url}
                />
              ))}
            </Routes>
          </Shell>
        </Scrollbar>
      </Flex>
    </Box>
  );
};
export default Policies;
